import { LitElement, html, TemplateResult, PropertyValueMap, nothing } from "lit";
import { state } from "lit/decorators.js";
import { ScrollBehavior } from './browser';
import environment from "../../common/environment";

declare global {
    interface CSSStyleSheet {
        replace: (text: string) => Promise<CSSStyleSheet>;
    }
    interface ShadowRoot {
        adoptedStyleSheets: Array<CSSStyleSheet>;
    }
}

// Feature detect constructable stylesheets.
let hasConstructableStylesheet = false;
try {
    new CSSStyleSheet();
    hasConstructableStylesheet = true;
} catch {
    hasConstructableStylesheet = false;
}

console.debug(hasConstructableStylesheet ? 'Using constructable stylesheets' : 'NOT using constructable stylesheets');

let theme: Promise<any> | undefined = undefined;
if (hasConstructableStylesheet) {
    /**
     * Request to retrieve css theme contents, in browser cached responses for
     * envs without the same domain (for example: QALV author or goldPub requesting qalv/mhcorp.css)
     * the request could be blocked by CORS due to the browser not sending headers in the request.
     * This method will try a standard request, if it fails it will request again
     * but with cache: 'reload' parameter to skip and recreate cache.
    */
    const fetchThemeFile = async (url) => {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Request status: ${response.status}`);
            }
            return await response.text();
        } catch (error) {
            console.warn('Constructable stylesheets rebuild cache request');
            try {
                const response = await fetch(url, { cache: 'reload' });
                if (!response.ok) {
                    throw new Error(`Request status: ${response.status}`);
                }
                return await response.text();
            } catch (error) {
                console.error('Constructable stylesheets rebuild cache request failed');
                return "";
            }
        }
    };
    theme = fetchThemeFile(environment().themeUrl).then(css => {
        return new CSSStyleSheet().replace(css);
    });
}

export class BaseComponent extends LitElement {
    @state()
    private ready = false;

    @state()
    uxtIconReady = false;

    @state()
    eventListenerList: any[] = [];

    /**
     * Implementing components should set the unloadedHeight to a value
     * that matches the expected height of the component after it loads.
     * This way, the layout shift that the user see's as the component loads
     * is minimized.
     */
    unloadedHeight = '0';

    constructor() {
        super();
    }

    protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
        super.firstUpdated(_changedProperties);
        
        if (process.env.UNIT_TEST) {
            this.ready = true;
            return;
        }
        
        if (hasConstructableStylesheet && theme) {
            // Apply the theme once it has been loaded.
            theme.then(sheet => {
                this.ready = true;
                if (this.shadowRoot) {
                    this.shadowRoot.adoptedStyleSheets = [...this.shadowRoot.adoptedStyleSheets, sheet];
                }
            });
        } else {
            // Since contructable stylesheets are not available, we will have to apply the theme with a <link> tag in the template.
            this.ready = true;
        }

        // Remove this after upgrading LitElement. See details here: https://github.mheducation.com/MHEducation/ecomm-ui/pull/202#issue-249839
        window.customElements.whenDefined('uxt-icon').then(() => this.uxtIconReady = true);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();

        /**
         * This method performs cleanup by removing all event listeners that were added and tracked in the eventListenerList.
         * it iterates through the eventListenerList, removing each tracked event listener from its respective element.
         */
        this.eventListenerList.forEach(({ element, event, handler }) => {
            element.removeEventListener(event, handler);
        });
    }

    public readonly render = (): TemplateResult => {
        // TODO: This uxt-mhcorp is no longer needed by UXT. However, some of our components target this class. We should remove this when we have the time.
            if (this.ready && hasConstructableStylesheet) {
            // Render the component as normal, since the theme has been applied with the constructable stylesheets.
            return html`
                <div class="uxt-mhcorp">
                    ${this.renderComp()}
                </div>
            `;
        } else if (this.ready && !hasConstructableStylesheet) {
            // Render the component with the theme applied via a <link> tag.
            return html`
                <link rel="stylesheet" type = "text/css" href="${environment().themeUrl}" />
                <div class="uxt-mhcorp">
                    ${this.renderComp()}
                </div>
            `;
        } else {
            // While the theme is loading, set the height to the specified value so that the layout shift is minimized.
            return html`
                <style>
                    :host {
                        min-height: ${this.unloadedHeight}
                    }
                </style>
            `;
        }
    }

    /* Needs overriden by implementing component */
    renderComp(): TemplateResult | typeof nothing{
        return nothing;
    }

    scrollTo() {
        if (!this.isInViewPort() && typeof this.scrollIntoView === 'function') {
           this.scrollIntoView({ behavior: ScrollBehavior.smooth });
        }
    }

    isInViewPort(element?: HTMLElement) {
        const bounding = element?.getBoundingClientRect() || this.getBoundingClientRect();
        return bounding.top >= 0 &&
               bounding.left >= 0 &&
               bounding.right <= (window.innerWidth || document.documentElement.clientWidth) &&
               bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight)
    }

    ensureElementVisible(element) {
        const parent = element.parentElement;
        if (!parent) return; // Element has no parent
    
        const parentStyles = window.getComputedStyle(parent);
        if (!(parentStyles.overflow.includes('auto') || parentStyles.overflow.includes('scroll'))) return; // Parent is not scrollable
    
        const parentBounding = parent.getBoundingClientRect();
        const elementBounding = element.getBoundingClientRect();
    
        if (
            elementBounding.top < parentBounding.top ||
            elementBounding.bottom > parentBounding.bottom ||
            elementBounding.left < parentBounding.left ||
            elementBounding.right > parentBounding.right
        ) {
            parent.scrollTo({
                top: elementBounding.top - parentBounding.top + parent.scrollTop,
                left: elementBounding.left - parentBounding.left + parent.scrollLeft,
                behavior: 'smooth'
            });
        }
    }

    /**
     * PLEASE DON'T USE THIS METHOD instead use bs5.mixin that contain the below code. This way we can keep the boostrap logic to seprate class.
     * Eg: export class Tabs extends connect(store)(bs5Connect(BaseComponent)) {}
     * 
     * TODO: NEED TO REMOVE THE METHOD
     */
     bsInitAll(query: string, bsComponent: any, config?: any, context?: Document | ShadowRoot | null) {
        const targetContext = context || this.shadowRoot;
        const isTruthy = !!bsComponent;
        const hasGetInstance = typeof bsComponent?.getInstance === 'function';
        const isConstructor = typeof bsComponent?.constructor === 'function';
        const instancesList: any = [];

        if (isTruthy && hasGetInstance && isConstructor && targetContext) {
            // For shadowRoot context, require uxt-mhcorp container
            const uxtMhcorp = targetContext === this.shadowRoot ? targetContext.querySelector('.uxt-mhcorp') : null;

            if (uxtMhcorp || targetContext !== this.shadowRoot) {
                for (const element of targetContext.querySelectorAll(query)) {
                    const instance = bsComponent.getInstance(element);
                    if (instance) {
                        instancesList.push({
                            firstInit: true,
                            instance
                        });
                    } else {
                        // For shadowRoot context, use uxt-mhcorp container, for other contexts use config as-is
                        const instanceConfig = uxtMhcorp 
                            ? { container: uxtMhcorp, ...config }
                            : config;

                        instancesList.push({
                            firstInit: false,
                            instance: new bsComponent(element, instanceConfig)
                        });
                    }
                }
            }
        } else if (!isTruthy) {
            console.error('bsComponent parameter provided to bsInit is not truthy: ', bsComponent);
        } else if (!hasGetInstance) {
            console.error('bsComponent parameter provided to bsInit does not have a getInstance method. This probable means it is not a bootstrap component class: ', bsComponent);
        } else if (!isConstructor) {
            console.error('bsComponent parameter provided to bsInit is not a constructor. This probable means it is not a bootstrap component class: ', bsComponent);
        }

        return instancesList;
    }

    /**
     * PLEASE DON'T USE THIS METHOD instead use bs5.mixin that contain the below code. This way we can keep the boostrap logic to seprate class.
     * Eg: export class Tabs extends connect(store)(bs5Connect(BaseComponent)) {}
     * 
     * TODO: NEED TO REMOVE THE METHOD
     */
     bsInit(query: string, bsComponent: any, config?: any, context?: Document | ShadowRoot | null) {
        const instanceList = this.bsInitAll(query, bsComponent, config, context);
        if(instanceList && instanceList.length > 0)
            return instanceList[0];
        else
            return undefined;
    }

    addTrackedEventListener(element: Element|Document|Window, event: string, handler: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions) {
        element.addEventListener(event, handler, options);
        this.eventListenerList.push({ element, event, handler });
    }
}
